import { ArrowRight, Loader2, Search, Users, X, Plus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getCustomers } from '@/modules/customer/api/customer.api'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { toast } from '@/shared/lib/toast'

function CreateQuoteContent({ onOpenChange }) {
  const navigate = useNavigate()

  const [customers, setCustomers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')

  const searchRef = useRef(null)

  useEffect(() => {
    let cancelled = false

    getCustomers({ limit: 100 })
      .then((res) => {
        if (!cancelled) setCustomers(res?.data?.customers || [])
      })
      .catch((error) => {
        if (!cancelled)
          toast.error(error?.message || 'Failed to load customers')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    const timer = setTimeout(() => searchRef.current?.focus(), 100)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [])

  const filteredCustomers = useMemo(() => {
    if (!search.trim()) return customers

    const q = search.toLowerCase().trim()

    return customers.filter(
      (l) =>
        (l.contactPersonName || '').toLowerCase().includes(q) ||
        (l.companyName || '').toLowerCase().includes(q) ||
        (l.contactPersonEmail || '').toLowerCase().includes(q) ||
        (l.contactPersonPhone || '').toLowerCase().includes(q),
    )
  }, [customers, search])

  const handleSelectCustomer = (customer) => {
    onOpenChange(false)
    const id = customer._id || customer.id
    navigate(`/sales/leads/create-quotation/cust_${id}`, {
      state: { customer },
    })
  }

  const handleCreateNewCustomer = () => {
    onOpenChange(false)
    navigate(`/sales/leads/create-quotation/new`)
  }

  return (
    <DialogContent className="w-[calc(100%-32px)] max-w-lg rounded-3xl p-6 shadow-2xl">
      <DialogHeader className="space-y-1">
        <div className="flex items-center gap-2.5">
          <div className="flex size-9 items-center justify-center rounded-xl bg-slate-900 text-white">
            <Users className="size-4" />
          </div>
          <div>
            <DialogTitle className="text-xl font-bold text-slate-900">
              Select Customer for Quotation
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Select a customer to auto-fetch details or create for a new one.
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div className="mt-2 flex w-full justify-end">
        <Button
          onClick={handleCreateNewCustomer}
          size="sm"
          className="w-full bg-gray-900 hover:bg-gray-800 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Quotation for New Customer
        </Button>
      </div>

      {/* Search input */}
      <div className="relative mt-3">
        <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-slate-400" />
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customer by name, company, email..."
          className="h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-9 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-slate-400 focus:bg-white focus:outline-none"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      {/* Customer list */}
      <div className="mt-2 max-h-[360px] overflow-y-auto rounded-2xl border border-slate-100 bg-slate-50/50 p-1.5">
        {isLoading ? (
          <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-slate-500">
            <Loader2 className="size-5 animate-spin text-slate-700" />
            <span>Loading customers...</span>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="flex h-36 flex-col items-center justify-center gap-1.5 text-center text-sm text-slate-500">
            <Users className="size-6 text-slate-300" />
            <p className="font-medium text-slate-700">
              {search
                ? 'No matching customers found'
                : 'No customers available'}
            </p>
            <p className="text-xs text-slate-400">
              {search
                ? 'Try searching with a different name, email, or company.'
                : 'Add customers in the Customer section first.'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredCustomers.map((customer) => {
              const id = customer._id || customer.id
              const initials = (
                customer.contactPersonName ||
                customer.companyName ||
                '?'
              )
                .split(' ')
                .map((w) => w[0])
                .filter(Boolean)
                .slice(0, 2)
                .join('')
                .toUpperCase()

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleSelectCustomer(customer)}
                  className="group flex w-full items-center justify-between gap-3 rounded-xl bg-white p-3 text-left shadow-xs transition-all hover:bg-slate-900 hover:shadow-md"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-800 transition-colors group-hover:bg-slate-800 group-hover:text-white">
                      {initials}
                    </div>

                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900 transition-colors group-hover:text-white">
                        {customer.companyName || 'Unnamed Company'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500 transition-colors group-hover:text-slate-300">
                        {customer.contactPersonName && (
                          <span className="truncate">
                            {customer.contactPersonName}
                          </span>
                        )}
                        {customer.contactPersonName &&
                          customer.contactPersonEmail && <span>•</span>}
                        {customer.contactPersonEmail && (
                          <span className="truncate">
                            {customer.contactPersonEmail}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <div className="flex size-7 items-center justify-center rounded-full bg-slate-100 text-slate-400 transition-colors group-hover:bg-slate-800 group-hover:text-white">
                      <ArrowRight className="size-3.5" />
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer info */}
      {!isLoading && filteredCustomers.length > 0 && (
        <div className="flex items-center justify-between text-xs text-slate-400 mt-2">
          <span>
            Showing {filteredCustomers.length} customer
            {filteredCustomers.length !== 1 ? 's' : ''}
          </span>
          <span>Click any customer to create quote</span>
        </div>
      )}
    </DialogContent>
  )
}

export default function CreateQuoteDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <CreateQuoteContent onOpenChange={onOpenChange} />}
    </Dialog>
  )
}
